import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { db, functions, auth } from './firebaseConfig';
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut, deleteUser } from 'firebase/auth';
import './CreatorProfile.css';

function CreatorProfile() {
  const { currentUser, userProfile, upgradeToCreator } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'settings'
  const [gameHistory, setGameHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Settings state
  const [displayName, setDisplayName] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  
  // Creator profile fields
  const [channelUrl, setChannelUrl] = useState('');
  const [promotionalUrl, setPromotionalUrl] = useState('');
  const [contentType, setContentType] = useState('');
  const [bio, setBio] = useState('');

  useEffect(() => {
    const loadData = async () => {
      // Load settings data
      if (userProfile) {
        setDisplayName(userProfile.displayName || '');
        setProfilePictureUrl(userProfile.photoURL || '');
        setChannelUrl(userProfile.creatorProfile?.channelUrl || '');
        setPromotionalUrl(userProfile.creatorProfile?.promotionalUrl || '');
        setContentType(userProfile.creatorProfile?.contentType || '');
        setBio(userProfile.creatorProfile?.bio || '');
      }
      
      await loadGameHistory();
      await loadConnectedPlatforms();
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const loadConnectedPlatforms = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const platforms = [];
        
        if (data.socialLinks) {
          Object.entries(data.socialLinks).forEach(([platform, url]) => {
            if (url) {
              platforms.push({
                platform,
                url,
                status: data.platformStatus?.[platform] || 'pending'
              });
            }
          });
        }
        
        setConnectedPlatforms(platforms);
      }
    } catch (error) {
      console.error('Error loading connected platforms:', error);
    }
  };

  const loadGameHistory = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      // Get recent game results for this user
      const resultsQuery = query(
        collection(db, 'gameResults'),
        where('userId', '==', currentUser.uid),
        orderBy('completedAt', 'desc')
      );

      const snapshot = await getDocs(resultsQuery);
      const history = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().completedAt?.toDate() || new Date(),
      }));

      // Group by game type and count
      const gameCounts = {};
      history.forEach(game => {
        let gameType = game.gameType || 'reaction';
        // Normalize reaction and reactionTest to the same key
        if (gameType === 'reactionTest') {
          gameType = 'reaction';
        }
        gameCounts[gameType] = (gameCounts[gameType] || 0) + 1;
      });

      setGameHistory(gameCounts);
      console.log('Loaded game history:', Object.keys(gameCounts).length, 'game types');
    } catch (error) {
      console.error('Error loading game history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Admin function to manually trigger winner calculation
  const handleManualTriggerWinner = async () => {
    if (!currentUser || currentUser.email !== 'ironwillerob@gmail.com') {
      alert('Admin access only');
      return;
    }

    const confirmed = window.confirm('Declare winner from current leaderboard?');
    if (!confirmed) return;

    try {
      const forceCalculateWinner = httpsCallable(functions, 'forceCalculateWinner');
      const result = await forceCalculateWinner({});
      
      if (result.data.success) {
        alert(`✅ WINNER DECLARED!\n\n${result.data.winnerName}\n\nRefreshing...`);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        alert('❌ No data found');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error: ' + error.message);
    }
  };

  // Admin function to check today's cycle data - REMOVED (permission issues)

  const handleTrackReferral = async (channelUrl) => {
    if (!channelUrl || !currentUser) return;
    
    try {
      // Track the click using Cloud Function
      const trackReferral = httpsCallable(functions, 'trackReferralClick');
      await trackReferral({ creatorId: currentUser.uid });
      
      console.log('Referral click tracked to:', channelUrl);
    } catch (error) {
      console.error('Error tracking referral:', error);
    }
    
    // Open in new tab
    window.open(channelUrl, '_blank');
  };

  const handleSaveSettings = async () => {
    if (!currentUser) return;
    
    try {
      setSettingsSaving(true);
      
      const userRef = doc(db, 'users', currentUser.uid);
      const updates = {};
      
      if (displayName) updates.displayName = displayName;
      if (profilePictureUrl) updates.photoURL = profilePictureUrl;
      
      // Update creator profile fields if user is a creator
      if (userProfile?.accountType === 'creator') {
        updates['creatorProfile.channelUrl'] = channelUrl;
        updates['creatorProfile.promotionalUrl'] = promotionalUrl || channelUrl;
        updates['creatorProfile.contentType'] = contentType;
        updates['creatorProfile.bio'] = bio;
        updates.promotionalURL = promotionalUrl || channelUrl; // Top-level field for compatibility
      }
      
      await updateDoc(userRef, updates);
      
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings: ' + error.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleUpgradeToCreator = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Upgrade your account to Creator? You can then complete your creator profile and appear on the leaderboard.')) return;
    
    try {
      setSettingsSaving(true);
      await upgradeToCreator();
      alert('Account upgraded to Creator! You can now complete your creator profile in the Profile tab.');
      window.location.reload(); // Refresh to show creator options
    } catch (error) {
      console.error('Error upgrading to creator:', error);
      alert('Failed to upgrade account: ' + error.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleConnectPlatform = async () => {
    if (!selectedPlatform || !currentUser) {
      alert('Please select a platform');
      return;
    }
    
    const platformUrl = prompt(`Enter your ${selectedPlatform} URL:`);
    if (!platformUrl) return;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        [`socialLinks.${selectedPlatform}`]: platformUrl,
        [`platformStatus.${selectedPlatform}`]: 'pending'
      });
      
      await loadConnectedPlatforms();
      setSelectedPlatform('');
      alert(`${selectedPlatform} connected! Status: Pending verification`);
    } catch (error) {
      console.error('Error connecting platform:', error);
      alert('Failed to connect platform');
    }
  };

  const handleRemovePlatform = async (platform) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove ${platform} connection?`)) return;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        [`socialLinks.${platform}`]: '',
        [`platformStatus.${platform}`]: ''
      });
      
      await loadConnectedPlatforms();
      alert(`${platform} removed`);
    } catch (error) {
      console.error('Error removing platform:', error);
      alert('Failed to remove platform');
    }
  };

  const handleSignOut = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to sign out?')) return;
    
    try {
      await signOut(auth);
      // Redirect handled by AuthContext
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out: ' + error.message);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    
    // eslint-disable-next-line no-restricted-globals
    const confirmDelete = confirm(
      'Are you sure you want to delete your account?\n\n' +
      'This will permanently delete:\n' +
      '• Your profile and settings\n' +
      '• Your game history\n' +
      '• Your creator profile (if applicable)\n' +
      '• All associated data\n\n' +
      'This action CANNOT be undone!'
    );
    
    if (!confirmDelete) return;
    
    // eslint-disable-next-line no-restricted-globals
    const finalConfirm = prompt(
      'Type "DELETE" to confirm permanent account deletion:'
    );
    
    if (finalConfirm !== 'DELETE') {
      alert('Account deletion cancelled.');
      return;
    }
    
    try {
      setSettingsSaving(true);
      
      // Delete user document from Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userRef);
      
      // Delete Firebase Auth account
      await deleteUser(currentUser);
      
      // Redirect to home
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      
      let errorMessage = 'Failed to delete account. ';
      
      if (error.code === 'auth/requires-recent-login') {
        errorMessage += 'For security, please sign out and sign in again, then try deleting your account.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
      setSettingsSaving(false);
    }
  };

  const openPrivacyPolicy = () => {
    window.open('/privacy-policy.html', '_blank');
  };

  const openTermsOfService = () => {
    window.open('/terms-of-service.html', '_blank');
  };

  const gamesPlayed = userProfile?.totalGamesPlayed || userProfile?.gamesPlayed || 0;
  const gamesWon = userProfile?.totalGamesWon || userProfile?.gamesWon || 0;

  const winRate = gamesPlayed > 0 
    ? ((gamesWon / gamesPlayed) * 100).toFixed(1) 
    : 0;

  const isCreator = userProfile?.accountType === 'creator' || userProfile?.isCreator;

  // Safety check: Wait for userProfile to load
  if (!userProfile) {
    return (
      <div className="creator-profile-container">
        <div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-6xl mx-auto p-6 ${isDarkMode ? '' : 'bg-neutral-50'}`}>
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${
            activeTab === 'profile'
              ? (isDarkMode ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white')
              : (isDarkMode ? 'bg-[#0F1535] text-gray-400 hover:text-white border border-white/10' : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200')
          }`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile
        </button>
        <button
          className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all ${
            activeTab === 'settings'
              ? (isDarkMode ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white')
              : (isDarkMode ? 'bg-[#0F1535] text-gray-400 hover:text-white border border-white/10' : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200')
          }`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </div>

      {/* Profile Tab Content */}
      {activeTab === 'profile' && (
        <>
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border mb-6`}>
        <div className="flex items-center gap-6">
          {userProfile.photoURL ? (
            <img src={userProfile.photoURL} alt={userProfile.displayName} className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-3xl text-white font-bold">
              {userProfile.displayName?.charAt(0) || '?'}
            </div>
          )}
        <div className="flex-1">
          <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{userProfile.displayName}</h1>
          <div className="flex items-center gap-2">
            {showEmail ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{userProfile.email}</p>
            ) : (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>••••••••••••••</p>
            )}
            <button 
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'} transition-colors`}
              onClick={() => setShowEmail(!showEmail)}
              title={showEmail ? "Hide email" : "Show email"}
            >
              {showEmail ? <><i className="fa-solid fa-eye-slash"></i> Hide</> : <><i className="fa-solid fa-eye"></i> Show</>}
            </button>
          </div>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${isCreator ? 'bg-yellow-500 text-white' : 'bg-blue-500 text-white'}`}>{isCreator ? '⭐ Creator' : '🎮 Player'}</span>
        </div>
      </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
          <div className="mb-3">
            <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>Games Played</div>
          </div>
          <div className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{userProfile.totalGamesPlayed || userProfile.gamesPlayed || 0}</div>
        </div>
        <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
          <div className="mb-3">
            <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>SubGames Won</div>
          </div>
          <div className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{userProfile.totalGamesWon || userProfile.gamesWon || 0}</div>
        </div>
        {isCreator && (
          <>
            <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
              <div className="mb-3">
                <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>Unique Supporters</div>
              </div>
              <div className={`text-4xl font-bold ${isDarkMode ? 'text-cyan-400' : 'text-blue-600'}`}>{userProfile.uniqueSupporters || 0}</div>
            </div>
            <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
              <div className="mb-3">
                <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>Games from Supporters</div>
              </div>
              <div className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{userProfile.gamesFromSupporters || 0}</div>
            </div>
          </>
        )}
        {!isCreator && (
          <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <div className="mb-3">
              <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>Win Rate</div>
            </div>
            <div className={`text-4xl font-bold ${isDarkMode ? 'text-cyan-400' : 'text-blue-600'}`}>{winRate}%</div>
          </div>
        )}
        <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
          <div className="mb-3">
            <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>Total Points</div>
          </div>
          <div className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{userProfile.totalPointsEarned || 0}</div>
        </div>
        {isCreator && (
          <div className={`stat-card ${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <div className="mb-3">
              <div className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-black'}`}>Channel Visits</div>
            </div>
            <div className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {userProfile.creatorProfile?.referralClicks || 0}
            </div>
          </div>
        )}
      </div>


      {/* Game History */}
      <div className="profile-section" style={{ 
        background: isDarkMode ? '#0F1535' : 'white',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
        paddingBottom: '10px'
      }}>
        <h2>Game Play Statistics</h2>
        {loading ? (
          <div className="loading">Loading statistics...</div>
        ) : Object.keys(gameHistory).length === 0 ? (
          <div className="empty-state">
            <p>No games played yet. Go play some minigames!</p>
          </div>
        ) : (
          <div className="game-stats-chart" style={{ padding: '10px 10px 5px 10px', overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: '270px', paddingBottom: '0' }}>
              {Object.entries(gameHistory)
                .sort((a, b) => b[1] - a[1])
                .map(([gameType, count]) => {
                  const maxCount = Math.max(...Object.values(gameHistory));
                  const barHeight = (count / maxCount) * 200;
                  const gameNames = {
                    'wordGuess': 'Word Guess',
                    'blockBlast': 'Block Blast',
                    'colorMatch': 'Color Match',
                    'memoryFlip': 'Memory Flip',
                    'whackAMole': 'Whack-A-Mole',
                    'patternPro': 'Pattern Pro',
                    'reaction': 'Reaction Test',
                    'reactionTest': 'Reaction Test'
                  };
                  return (
                    <div key={gameType} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', minWidth: '70px' }}>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold',
                        color: isDarkMode ? '#fff' : '#000',
                        minHeight: '24px'
                      }}>
                        {count}
                      </div>
                      <div style={{
                        width: '50px',
                        height: `${barHeight}px`,
                        background: 'linear-gradient(to top, #3b82f6, #8b5cf6)',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}></div>
                      <div style={{ 
                        fontSize: '11px',
                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                        textAlign: 'center',
                        width: '70px',
                        lineHeight: '1.2',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center'
                      }}>
                        {gameNames[gameType] || gameType}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Combined Profile Settings Card */}
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border space-y-6`}>
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Profile Settings</h3>
            
            {/* Display Name */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Your display name"
              />
            </div>

            {/* Connect Platforms - Only for Creators */}
            {userProfile?.accountType === 'creator' && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Connect Your Platforms</label>
                <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Link your content platforms. This will automatically import your profile picture and info.
                </p>
                
                <div className="space-y-3">
                  <select
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select a platform...</option>
                    <option value="youtube">YouTube</option>
                    <option value="twitch">Twitch</option>
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                  </select>
                  {selectedPlatform && (
                    <button onClick={handleConnectPlatform} className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors bg-blue-500 hover:bg-blue-600">
                      Connect {selectedPlatform}
                    </button>
                  )}
                </div>

                {/* Connected Platforms */}
                {connectedPlatforms.length > 0 && (
                  <div className="mt-4">
                    <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Connected Platforms:</h4>
                    {connectedPlatforms.map((platform) => (
                      <div key={platform.platform} className={`flex items-center justify-between p-3 rounded-lg mb-2 ${isDarkMode ? 'bg-[#1A1F3E]' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          <span>
                            {platform.platform === 'youtube' && (
                              <img src="/images/youtube-icon.png" alt="YouTube" className="w-6 h-6" />
                            )}
                            {platform.platform === 'twitch' && (
                              <img src="/images/twitch-icon.png" alt="Twitch" className="w-6 h-6" />
                            )}
                            {platform.platform === 'tiktok' && (
                              <img src="/images/tiktok-icon.png" alt="TikTok" className="w-6 h-6" />
                            )}
                            {platform.platform === 'instagram' && (
                              <img src="/images/instagram-icon.png" alt="Instagram" className="w-6 h-6" />
                            )}
                          </span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-1 rounded ${platform.status === 'pending' ? 'bg-yellow-500/20 text-yellow-600' : 'bg-green-500/20 text-green-600'} font-semibold`}>
                            {platform.status.toUpperCase()}
                          </span>
                          <button
                            onClick={() => handleRemovePlatform(platform.platform)}
                            className="px-3 py-1 rounded text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile Picture URL */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Profile Picture URL (Optional)</label>
              <input
                type="url"
                value={profilePictureUrl}
                onChange={(e) => setProfilePictureUrl(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="https://example.com/your-picture.jpg"
              />
            </div>

            {/* Creator Profile Fields - Only for Creators */}
            {isCreator && (
              <>
                {/* Channel URL */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Channel URL</label>
                  <input
                    type="url"
                    value={channelUrl}
                    onChange={(e) => setChannelUrl(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="https://youtube.com/@yourchannel"
                  />
                  <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Your main channel or profile URL
                  </p>
                </div>

                {/* Promotional URL */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Promotional URL (Linktree, etc.)</label>
                  <input
                    type="url"
                    value={promotionalUrl}
                    onChange={(e) => setPromotionalUrl(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="https://linktr.ee/yourprofile"
                  />
                  <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Optional: Link viewers will visit when you win
                  </p>
                </div>

                {/* Content Type */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Content Type</label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select content type</option>
                    <option value="Gaming">Gaming</option>
                    <option value="Vlog">Vlog</option>
                    <option value="Tutorial">Tutorial</option>
                    <option value="Comedy">Comedy</option>
                    <option value="Reactions">Reactions</option>
                    <option value="Reviews">Reviews</option>
                    <option value="Health & Wellness">Health & Wellness</option>
                    <option value="Beauty & Fashion">Beauty & Fashion</option>
                    <option value="Music">Music</option>
                    <option value="Art & Design">Art & Design</option>
                    <option value="Education">Education</option>
                    <option value="Talk/Podcast">Talk/Podcast</option>
                    <option value="IRL">IRL</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Bio - Separate Card for Better Layout */}
          {isCreator && (
            <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Bio</h3>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                placeholder="Tell viewers about yourself and your content..."
              />
            </div>
          )}

          {/* Dark Mode Toggle */}
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Appearance</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: isDarkMode ? '#2d2d2d' : '#f5f5f5', borderRadius: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', flex: 1, color: isDarkMode ? '#e0e0e0' : '#000' }}>Dark Mode</span>
              <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isDarkMode}
                  onChange={toggleTheme}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: isDarkMode ? '#4CAF50' : '#ccc',
                  transition: '0.4s',
                  borderRadius: '26px'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '18px',
                    width: '18px',
                    left: isDarkMode ? '28px' : '4px',
                    bottom: '4px',
                    background: 'white',
                    transition: '0.4s',
                    borderRadius: '50%'
                  }}></span>
                </span>
              </label>
            </div>
          </div>

          {/* Upgrade to Creator (only for players) */}
          {!isCreator && (
            <div className="profile-section">
              <h3>⭐ Become a Creator</h3>
              <p className="section-description">
                Upgrade your account to Creator status to join the leaderboard, add your channel, and compete for daily prizes!
              </p>
              <button
                onClick={handleUpgradeToCreator}
                className="btn-upgrade-creator"
                disabled={settingsSaving}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: settingsSaving ? 'not-allowed' : 'pointer',
                  opacity: settingsSaving ? 0.7 : 1,
                }}
              >
                {settingsSaving ? 'Upgrading...' : '⭐ Upgrade to Creator'}
              </button>
            </div>
          )}

          {/* Save Settings Button */}
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <button
              onClick={handleSaveSettings}
              className="w-full py-3 px-6 rounded-lg font-bold text-white transition-colors bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:opacity-50"
              disabled={settingsSaving}
            >
              {settingsSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {/* Account Information */}
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Account Information</h3>
            <div>
              <input
                type="text"
                value={currentUser?.uid || ''}
                className={`w-full px-4 py-3 rounded-lg border ${isDarkMode ? 'bg-[#1A1F3E] border-white/10 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-600'} cursor-not-allowed`}
                disabled
              />
            </div>
          </div>

          {/* Legal & Privacy */}
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Legal & Privacy</h3>
            <div className="space-y-3">
              <button onClick={openPrivacyPolicy} className={`w-full py-3 px-4 rounded-lg text-left transition-colors ${isDarkMode ? 'bg-[#1A1F3E] hover:bg-[#252B4A] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}>
                📄 Privacy Policy
              </button>
              <button onClick={openTermsOfService} className={`w-full py-3 px-4 rounded-lg text-left transition-colors ${isDarkMode ? 'bg-[#1A1F3E] hover:bg-[#252B4A] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}>
                📋 Terms of Service
              </button>
            </div>
          </div>

          {/* Account Actions */}
          <div className={`${isDarkMode ? 'bg-[#0F1535] border-white/10' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Account Actions</h3>
            
            {/* Admin-only button */}
            {currentUser?.email === 'ironwillerob@gmail.com' && (
              <button 
                onClick={handleManualTriggerWinner} 
                className="w-full py-3 px-6 rounded-lg font-bold text-white transition-colors bg-red-500 hover:bg-red-600 mb-3"
              >
                🏆 DECLARE WINNER NOW
              </button>
            )}
            
            <button onClick={handleSignOut} className="w-full py-3 px-6 rounded-lg font-bold text-white transition-colors bg-red-500 hover:bg-red-600 mb-3">
              🚪 Sign Out
            </button>
            
            <div className="flex justify-start mt-4">
              <button 
                onClick={handleDeleteAccount} 
                className={`text-xs py-1.5 px-3 rounded font-medium transition-colors ${isDarkMode ? 'bg-red-900/30 hover:bg-red-900/50 text-red-300 border border-red-600/50' : 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-300'}`}
                disabled={settingsSaving}
              >
                🗑️ Delete Account
              </button>
            </div>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              ⚠️ Account deletion is permanent
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatorProfile;
